// ============================================================================
// NewCommitteeDialog — wizard 3 etapes « Nouveau comite » (E3a, F-403/F-404)
// ============================================================================
// Portage du PoC new-committee-dialog.tsx (WizardDialog) : 3 etapes
// name -> description -> statut+date cible, puis createCommitteeAction.
// C-native : modal locale (tokens DS, gabarit ConfirmDialog @gs/ui) + mutation
// useCreateCommittee ; succes = drill-in fiche (parite redirect /comites/{id}).
// Visible ADMIN seulement (F-403 — affordance viewer.canCreate, garde serveur).

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';
import {
  useCreateCommittee, committeeErrorCode, type CommitteeStatus,
} from '../../hooks/useCommittees';

const STEPS = ['name', 'description', 'meta'] as const;
type Step = (typeof STEPS)[number];


export function NewCommitteeDialog({
  open,
  onClose,
  onError,
}: {
  open: boolean;
  onClose: () => void;
  /** Remonte le code court d'erreur a la page (alerte ?error= — F-402). */
  onError: (code: string) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateCommittee();

  const [stepIndex, setStepIndex] = useState(0);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<CommitteeStatus>('active');
  const [targetDate, setTargetDate] = useState('');
  const [stepError, setStepError] = useState<string | null>(null);

  if (!open) return null;

  const step: Step = STEPS[stepIndex];
  const isLast = stepIndex === STEPS.length - 1;

  function reset() {
    setStepIndex(0);
    setName('');
    setDescription('');
    setStatus('active');
    setTargetDate('');
    setStepError(null);
  }

  function close() {
    reset();
    onClose();
  }

  function next() {
    // Validation d'etape (parite wizard PoC : le nom est requis a l'etape 1).
    if (step === 'name' && !name.trim()) {
      setStepError(t('gest:comites.wizard.nameRequired'));
      return;
    }
    setStepError(null);
    if (!isLast) {
      setStepIndex((i) => i + 1);
      return;
    }
    create.mutate(
      {
        name: name.trim(),
        description: description.trim() ? description.trim() : null,
        status,
        targetDate: targetDate || null,
      },
      {
        onSuccess: ({ id }) => {
          close();
          navigate(`/comites/${id}`); // parite PoC : redirect fiche apres creation
        },
        onError: (err) => {
          close();
          onError(committeeErrorCode(err));
        },
      },
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('gest:comites.wizard.title')}
    >
      <div className="flex max-h-full w-full max-w-md flex-col gap-4 overflow-y-auto rounded-xl border border-slate-200 bg-white p-5 shadow-md dark:border-slate-700 dark:bg-slate-800">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-body font-semibold text-slate-900 dark:text-slate-100">
              {t('gest:comites.wizard.title')}
            </h2>
            <p className="mt-0.5 text-meta text-slate-500 dark:text-slate-400">
              {t('gest:comites.wizard.stepOf', { current: stepIndex + 1, total: STEPS.length })}
              {' — '}
              {t(`gest:comites.wizard.step.${step}`)}
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label={t('gest:comites.wizard.cancel')}
            className="touch-manipulation rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        {step === 'name' ? (
          <div className="flex flex-col gap-2">
            <label htmlFor="new-committee-name" className="text-meta font-medium text-slate-600 dark:text-slate-300">
              {t('gest:comites.wizard.nameLabel')}
            </label>
            <input
              id="new-committee-name"
              autoFocus
              maxLength={200}
              placeholder={t('gest:comites.wizard.namePlaceholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`${GS_COMPONENTS.input}`}
            />
          </div>
        ) : null}

        {step === 'description' ? (
          <div className="flex flex-col gap-2">
            <label htmlFor="new-committee-description" className="text-meta font-medium text-slate-600 dark:text-slate-300">
              {t('gest:comites.wizard.descriptionLabel')}
            </label>
            <textarea
              id="new-committee-description"
              rows={4}
              maxLength={4000}
              placeholder={t('gest:comites.wizard.descriptionPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={`${GS_COMPONENTS.input}`}
            />
          </div>
        ) : null}

        {step === 'meta' ? (
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="new-committee-status" className="text-meta font-medium text-slate-600 dark:text-slate-300">
                {t('gest:comites.wizard.statusLabel')}
              </label>
              <select
                id="new-committee-status"
                value={status}
                onChange={(e) => setStatus(e.target.value as CommitteeStatus)}
                className={`${GS_COMPONENTS.input} w-36`}
              >
                <option value="active">{t('gest:comites.status.active')}</option>
                <option value="paused">{t('gest:comites.status.paused')}</option>
                <option value="completed">{t('gest:comites.status.completed')}</option>
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="new-committee-date" className="text-meta font-medium text-slate-600 dark:text-slate-300">
                {t('gest:comites.wizard.targetDateLabel')}
              </label>
              <input
                id="new-committee-date"
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className={`${GS_COMPONENTS.input} w-44`}
              />
            </div>
          </div>
        ) : null}

        {stepError ? (
          <p role="alert" className="text-field text-red-600 dark:text-red-400">{stepError}</p>
        ) : null}

        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => (stepIndex === 0 ? close() : setStepIndex((i) => i - 1))}
            className={`${GS_COMPONENTS.btnSecondary} touch-manipulation`}
          >
            {stepIndex === 0 ? t('gest:comites.wizard.cancel') : t('gest:comites.wizard.back')}
          </button>
          <button
            type="button"
            onClick={next}
            disabled={create.isPending}
            className={GS_COMPONENTS.btnAction}
          >
            {isLast ? t('gest:comites.wizard.create') : t('gest:comites.wizard.next')}
          </button>
        </div>
      </div>
    </div>
  );
}
